// CR-005 items 3 and 4 (J-191): where a screen opens when no single ACO is chosen, and where sign-in lands.
// Both follow the ACOs metadata lists, which hold no retired ACO: the first with calculated results. An ACO
// without data is never a default (J-200: production opened an empty demo ACO). Production opens no
// demonstration page. J-209 (the owner's inbox file 06, PR D items 9, 10 and 12): the default year is the
// newest with results, and an account with no ACO to open lands on the no-access page.
import { ALL_ACOS, type Metadata } from "@vbltc/schema";

type MetadataAco = Pick<Metadata["acos"][number], "acoCode" | "hasData" | "dataYears">;

/** The page for an account with no ACO to open (J-209 items 9 and 10). */
const NO_ACCESS_PATH = "/no-access";

/**
 * `aco` when no single ACO is chosen: every ACO in the caller's scope. J-271: the server's quality
 * report reads take the same value (`@vbltc/schema` ALL_ACOS), so it is defined there once.
 */
export { ALL_ACOS } from "@vbltc/schema";

/** Whether the ACO has calculated results: for `year`, or for any year when `year` is undefined. */
export const acoHasData = (a: MetadataAco, year?: number): boolean =>
  year === undefined ? a.hasData : a.dataYears.includes(year);

/** The ACOs with results for `year`, in metadata's order (J-209 item 2: Performance's "All"). */
export function acosWithData(acos: readonly MetadataAco[], year: number): string[] {
  return acos.filter((a) => acoHasData(a, year)).map((a) => a.acoCode);
}

/**
 * The first ACO in the caller's scope with data, for `year` when given, else undefined. With a year
 * that no ACO has data for, the first ACO with any data, so "All" still opens a real ACO.
 */
export function defaultAcoCode(acos: readonly MetadataAco[], year?: number): string | undefined {
  const forYear = year === undefined ? undefined : acos.find((a) => acoHasData(a, year));
  return forYear?.acoCode ?? acos.find((a) => a.hasData)?.acoCode;
}

/**
 * J-209 item 12: the year a screen opens on when the URL names none. One ACO: its newest year with
 * results. "All" (or no ACO): the newest year any ACO in scope has results for. With no results, the
 * newest configured year.
 */
export function defaultYear(
  metadata: Pick<Metadata, "years" | "acos"> | undefined,
  aco: string | undefined,
): number | undefined {
  if (metadata === undefined) return undefined;
  const chosen =
    aco === undefined || aco === ALL_ACOS
      ? metadata.acos
      : metadata.acos.filter((a) => a.acoCode === aco);
  const newest = Math.max(...chosen.flatMap((a) => a.dataYears));
  return Number.isFinite(newest) ? newest : metadata.years[0];
}

/** The URL's year, else `defaultYear`. */
export const resolveYear = (
  metadata: Pick<Metadata, "years" | "acos"> | undefined,
  year: number | undefined,
  aco: string | undefined,
): number | undefined => year ?? defaultYear(metadata, aco);

/**
 * The page after sign-in: the default ACO's summary. With no ACO with data, the Data page in production
 * and the Playground elsewhere for a role that loads data; every other account, and one metadata refuses
 * (no TENANT assignment, or a role that reads no ACO screen: `undefined`), the no-access page (J-209).
 */
export function landingPath(
  metadata: Pick<Metadata, "acos" | "appEnv"> | undefined,
  canLoadData: boolean,
): string {
  if (metadata === undefined) return NO_ACCESS_PATH;
  const code = defaultAcoCode(metadata.acos);
  if (code !== undefined) return `/aco/${encodeURIComponent(code)}`;
  if (!canLoadData) return NO_ACCESS_PATH;
  return isProduction(metadata) ? "/data" : "/playground";
}

/** Whether the server runs as production, where the Playground and the legacy controls do not exist. */
export const isProduction = (metadata: Pick<Metadata, "appEnv"> | undefined): boolean =>
  metadata?.appEnv === "prod";

/**
 * J-209 item 16: hide what production does not have until the server has said it is not production, so
 * a production screen never flashes it while metadata loads or after metadata fails.
 */
export const knownNotProduction = (metadata: Pick<Metadata, "appEnv"> | undefined): boolean =>
  metadata !== undefined && !isProduction(metadata);
